/**
 * CierreInactividad.tsx — Cierra la sesión tras 5 minutos sin tocar la app,
 * esté en la pantalla que esté, y lleva al login.
 *
 * Va una sola vez en App.tsx, dentro del Router. Cuenta desde el último toque,
 * tecla o desplazamiento en cualquier parte (también dentro de los modales).
 * Mide con la hora real y no con un temporizador suelto: si el teléfono se
 * bloquea o la app pasa a segundo plano, al volver se revisa cuánto tiempo pasó
 * y, si fueron 5 minutos o más, se cierra en ese momento.
 *
 * 30 segundos antes avisa con una barra; cualquier toque la quita y reinicia la cuenta.
 *
 * No actúa en el login ni en las pantallas de pago (/r/, /pasarela, /confirmacionpago):
 * el cliente sale a Payphone o De Una y vuelve con un enlace; cortar ahí podría
 * perder la confirmación del pago.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { App as CapacitorApp } from '@capacitor/app';
import { olvidarDetalleAbierto } from '../utils/detalleClienteAbierto';

const INACTIVIDAD_MAXIMA_MS = 5 * 60 * 1000;
const AVISO_ANTES_MS = 30 * 1000;
const REVISAR_CADA_MS = 1000;

// Dónde no se cierra la sesión por inactividad
const RUTAS_EXENTAS = [/^\/$/, /^\/r\//, /^\/pasarela/, /^\/confirmacionpago/];

// Todo lo que cuenta como "el usuario está usando la app"
const EVENTOS_ACTIVIDAD = ['pointerdown', 'touchstart', 'keydown', 'wheel', 'scroll'] as const;

/** Estado de la navegación que el login lee para explicar por qué se cerró la sesión. */
export const MOTIVO_INACTIVIDAD = 'inactividad';

const haySesion = (): boolean => {
  try { return Boolean(localStorage.getItem('clientData')); } catch { return false; }
};

const CierreInactividad: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const ultimaActividad = useRef(Date.now());
  const rutaRef = useRef(pathname);
  rutaRef.current = pathname;

  // Segundos que faltan para el cierre; null = sin aviso visible
  const [segundos, setSegundos] = useState<number | null>(null);
  const segundosRef = useRef<number | null>(null);

  const mostrarAviso = (valor: number | null) => {
    if (segundosRef.current === valor) return;
    segundosRef.current = valor;
    setSegundos(valor);
  };

  // Entrar a otra pantalla también es actividad
  useEffect(() => { ultimaActividad.current = Date.now(); }, [pathname]);

  useEffect(() => {
    const marcarActividad = () => {
      ultimaActividad.current = Date.now();
      if (segundosRef.current !== null) mostrarAviso(null);
    };

    const revisar = () => {
      const exenta = RUTAS_EXENTAS.some(r => r.test(rutaRef.current));
      if (exenta || !haySesion()) {
        // En una pantalla exenta la cuenta no corre: empieza de nuevo al salir de ella
        ultimaActividad.current = Date.now();
        mostrarAviso(null);
        return;
      }
      const inactivo = Date.now() - ultimaActividad.current;
      if (inactivo >= INACTIVIDAD_MAXIMA_MS) {
        mostrarAviso(null);
        localStorage.removeItem('clientData');
        olvidarDetalleAbierto();
        ultimaActividad.current = Date.now();
        navigate('/', { replace: true, state: { motivo: MOTIVO_INACTIVIDAD } });
        return;
      }
      const restante = INACTIVIDAD_MAXIMA_MS - inactivo;
      mostrarAviso(restante <= AVISO_ANTES_MS ? Math.ceil(restante / 1000) : null);
    };

    // capture: también los toques dentro de modales y listas que frenan la propagación
    EVENTOS_ACTIVIDAD.forEach(e => window.addEventListener(e, marcarActividad, { capture: true, passive: true }));
    const intervalo = window.setInterval(revisar, REVISAR_CADA_MS);

    // Al volver del segundo plano el intervalo pudo estar dormido: se revisa enseguida
    const alCambiarVisibilidad = () => { if (document.visibilityState === 'visible') revisar(); };
    document.addEventListener('visibilitychange', alCambiarVisibilidad);
    const escuchaApp = CapacitorApp.addListener('appStateChange', ({ isActive }) => { if (isActive) revisar(); });

    return () => {
      EVENTOS_ACTIVIDAD.forEach(e => window.removeEventListener(e, marcarActividad, { capture: true }));
      window.clearInterval(intervalo);
      document.removeEventListener('visibilitychange', alCambiarVisibilidad);
      escuchaApp.then(e => e.remove());
    };
    // navigate es estable dentro del Router
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (segundos === null) return null;

  return (
    <div
      role="alert"
      style={{
        position: 'fixed', left: 12, right: 12, zIndex: 2000,
        top: 'calc(env(safe-area-inset-top, 0px) + 10px)',
        maxWidth: 440, margin: '0 auto', padding: '10px 12px', borderRadius: 12,
        display: 'flex', alignItems: 'center', gap: 10,
        background: 'var(--sn-alerta-suave)', color: 'var(--sn-alerta-texto)',
        border: '1px solid var(--sn-alerta)', boxShadow: 'var(--sn-sombra)',
        fontSize: 13, fontWeight: 600,
      }}
    >
      <span aria-hidden="true" style={{ fontSize: 18 }}>⏱️</span>
      <span style={{ flex: 1 }}>
        Por inactividad, tu sesión se cerrará en <strong>{segundos} s</strong>.
      </span>
      <button
        type="button"
        style={{
          flexShrink: 0, minHeight: 36, padding: '6px 12px', borderRadius: 10,
          border: '1.5px solid var(--sn-alerta)', background: 'var(--sn-superficie)',
          color: 'var(--sn-texto)', fontSize: 12, fontWeight: 700, cursor: 'pointer',
        }}
        // El toque ya cuenta como actividad (se capta arriba); el botón es para que se entienda
        onClick={() => mostrarAviso(null)}
      >
        Seguir conectado
      </button>
    </div>
  );
};

export default CierreInactividad;
